// Shared Field Sales formatters + month helpers, mirroring the reference
// dashboard (SalesPulse) so wording and number formats match exactly.
//
// Monetary values from the API are in LAKHS. formatValueAuto reproduces the
// reference's formatValueAuto(): ₹X.XX Cr when the lakh value is >= 100, else
// ₹X.XX L. KPI sales/target and HQ-level values both go through this (Cr vs L
// is chosen by magnitude, exactly like the HTML).

/** Indian-grouped integer, e.g. 11255 -> "11,255" (em dash for non-finite). */
export function formatIndianInt(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const num = Number(n);
  const neg = num < 0;
  const abs = Math.round(Math.abs(num));
  const s = String(abs);
  if (s.length <= 3) return (neg ? '-' : '') + s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  return (neg ? '-' : '') + rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

/**
 * ₹ value with auto Cr/L suffix, from a LAKH input (reference formatValueAuto).
 *   >= 100 L  -> "₹X.XX Cr"  (value / 100)
 *   <  100 L  -> "₹X.XX L"
 * @param {number} lakhs
 */
export function formatValueAuto(lakhs) {
  if (lakhs == null || !Number.isFinite(Number(lakhs))) return '—';
  const v = Number(lakhs);
  const neg = v < 0;
  const a = Math.abs(v);
  if (a >= 100) {
    return (neg ? '-' : '') + '₹' + (a / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr';
  }
  return (neg ? '-' : '') + '₹' + a.toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' L';
}

/** One-decimal percent; optional leading + sign for positives (reference formatPct). */
export function formatPct(v, sign = false) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return (sign && n > 0 ? '+' : '') + n.toFixed(1) + '%';
}

/** 'up' | 'down' | 'flat' from a signed delta (reference deltaClass). */
export function deltaClass(v) {
  if (v == null || !Number.isFinite(Number(v)) || Math.abs(Number(v)) < 0.01) return 'flat';
  return Number(v) > 0 ? 'up' : 'down';
}

/** ▲ / ▼ / → arrow (reference deltaArrow). */
export function deltaArrow(v) {
  if (v == null || !Number.isFinite(Number(v)) || Math.abs(Number(v)) < 0.01) return '→';
  return Number(v) > 0 ? '▲' : '▼';
}

// Delta text colours (reference --good / --bad / --muted).
export const DELTA_COLOR = {
  up: '#16A34A',
  down: '#DC2626',
  flat: '#6B7280',
};

// ── Shared table cell styles (Field Sales family) ──────────────────────────
// Every Field Sales tab/modal table shares these so a density/font change is
// made in ONE place. Font sizes are the app-wide "zoomed in" sizes (header
// 13px, body 14px) and horizontal padding is tightened to match the global
// MuiTableCell theme override. Alignment stays per-cell on each column.
export const FS_HEAD_SX = {
  fontSize: '13px',
  fontWeight: 700,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '.3px',
  whiteSpace: 'nowrap',
  bgcolor: '#fff',
  px: '10px',
};
export const FS_CELL_SX = {
  fontSize: '14px',
  color: '#1B2433',
  whiteSpace: 'nowrap',
  px: '10px',
};

/** Achievement slab tone class name (reference achCls). */
export function achSlab(p) {
  const n = Number(p);
  if (!Number.isFinite(n)) return 'mid';
  if (n >= 100) return 'good';
  if (n >= 90) return 'mid';
  if (n >= 80) return 'warn';
  return 'bad';
}

/** Achievement text colour tone (reference achColor): up / '' / down. */
export function achDelta(p) {
  const n = Number(p);
  if (!Number.isFinite(n)) return 'flat';
  if (n >= 100) return 'up';
  if (n >= 80) return 'flat';
  return 'down';
}

/** Achievement bar fill class (reference achBarClass): good / mid / bad. */
export function achBar(p) {
  const n = Number(p);
  if (!Number.isFinite(n)) return 'mid';
  if (n >= 100) return 'good';
  if (n >= 80) return 'mid';
  return 'bad';
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Wire "YYYYMM" -> UI "YYYY-MM" (pass-through otherwise). */
export function monthWireToUi(ym) {
  if (!ym) return null;
  const m = /^(\d{4})(\d{2})$/.exec(String(ym));
  return m ? `${m[1]}-${m[2]}` : String(ym);
}

/** 0-based month index from "YYYY-MM" or "YYYYMM" (null when unexpected). */
export function monthIndex(ym) {
  const m = /^(\d{4})-?(\d{2})$/.exec(String(ym));
  if (!m) return null;
  const idx = Number(m[2]) - 1;
  return idx >= 0 && idx <= 11 ? idx : null;
}

/** Short label "Aug" from "YYYY-MM"/"YYYYMM". */
export function monthShort(ym) {
  const i = monthIndex(ym);
  return i == null ? String(ym) : MONTH_NAMES[i];
}

/** Full label "August" from "YYYY-MM"/"YYYYMM". */
export function monthFull(ym) {
  const i = monthIndex(ym);
  return i == null ? String(ym) : MONTH_FULL[i];
}

/**
 * The reference periodLabel(): the token used in every "{Mon}" title.
 *   - no months        -> '—'  (callers that know the resolved month pass it in)
 *   - 1 month          -> short name, e.g. "Aug"
 *   - 2-3 months       -> "Apr+May+Jun"
 *   - more             -> "N months"
 * Accepts an array of "YYYY-MM" (or "YYYYMM") strings.
 */
export function periodLabel(months) {
  const list = (months ?? []).filter(Boolean);
  if (!list.length) return '—';
  if (list.length === 1) return monthShort(list[0]);
  if (list.length <= 3) return list.map(monthShort).join('+');
  return `${list.length} months`;
}
